<template>
  <div class="card p-3">
    <h2>Annullamento/Sospensione Notifiche CMOR</h2>

    <div class="mb-3 flex gap-3 align-items-center">
      <div class="flex flex-column">
        <label for="year" class="mb-2">Anno</label>
        <Dropdown
          id="year"
          v-model="selectedYear"
          :options="years"
          optionLabel="label"
          optionValue="value"
          placeholder="Seleziona anno"
          class="w-full md:w-14rem"
        />
      </div>

      <div class="flex flex-column">
        <label for="months" class="mb-2">Mesi</label>
        <MultiSelect
          id="months"
          v-model="selectedMonths"
          :options="months"
          optionLabel="label"
          optionValue="value"
          placeholder="Seleziona mesi"
          :maxSelectedLabels="3"
          class="w-full md:w-20rem"
        />
      </div>

      <Button
        label="Cerca"
        icon="pi pi-search"
        @click="loadData"
        :disabled="!isSearchEnabled"
        class="mt-4"
      />
    </div>
    <div class="mb-3 flex gap-3 align-items-end">
      <div class="flex flex-column flex-grow-1">
        <label for="search" class="mb-2">Ricerca</label>
        <span class="p-input-icon-left w-full">
          <i class="pi pi-search" />
          <InputText
            id="search"
            v-model="searchQuery"
            placeholder="Cerca per code, nome cliente o agente..."
            class="w-full"
          />
        </span>
      </div>

      <div class="flex flex-column">
        <label for="tipoFilter" class="mb-2">Tipo</label>
        <Dropdown
          id="tipoFilter"
          v-model="tipoFilter"
          :options="tipoFilterOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Filtra per tipo"
          class="w-full md:w-14rem"
        />
      </div>

      <div class="flex flex-column">
        <label for="lavorataFilter" class="mb-2">Stato Lavorazione</label>
        <Dropdown
          id="lavorataFilter"
          v-model="lavorataFilter"
          :options="lavorataFilterOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Filtra per stato"
          class="w-full md:w-14rem"
        />
      </div>

      <Button
        label="Invia Selezionati"
        icon="pi pi-send"
        @click="showConfirmDialog"
        :disabled="!selectedItems.length"
        class="mt-4"
      />

      <Button
        label="Storico File"
        icon="pi pi-history"
        @click="loadFileHistory"
        class="mt-4"
      />

      <Button
        :label="allSelected ? 'Deseleziona tutti' : 'Seleziona tutti'"
        :icon="allSelected ? 'pi pi-times' : 'pi pi-check-square'"
        :class="allSelected ? 'p-button-danger' : 'p-button-success'"
        @click="toggleSelectAll"
        style="align-self: flex-end;"
      />
    </div>

    <DataTable
      :value="filteredItems"
      :paginator="true"
      :rows="50"
      :loading="loading"
      dataKey="id"
      paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      :rowsPerPageOptions="[50, 100, 150]"
      currentPageReportTemplate="{first} a {last} di {totalRecords}"
      responsiveLayout="scroll"
    >
      <template #empty>
        <div class="text-center">Nessun dato trovato.</div>
      </template>
      <Column headerStyle="width: 3rem">
        <template #header>
          <div style="display: flex; flex-direction: column; align-items: center;">
            <span style="font-size: 0.85rem;"></span>
          </div>
        </template>
        <template #body="{ data }">
          <div v-if="data.lavorata !== 1">
            <Button
              :icon="selectedItems.some(item => item.id === data.id) ? 'pi pi-check-square' : 'pi pi-square'"
              class="p-button-text p-button-sm"
              @click="toggleSelection(data)"
              :label="selectedItems.some(item => item.id === data.id) ? 'Selezionato' : 'Seleziona'"
              :style="selectedItems.some(item => item.id === data.id) ? 'color: #2196F3; font-weight: bold;' : 'color: #888;'"
              :aria-label="selectedItems.some(item => item.id === data.id) ? 'Deseleziona' : 'Seleziona'"
            />
          </div>
        </template>
      </Column>
      <Column field="code" header="Code" :sortable="true">
        <template #body="{ data }">
          <span 
            class="cursor-pointer hover:text-primary"
            @click="goToDetail(data)"
          >
            {{ data.code }}
          </span>
        </template>
      </Column>
      <Column field="cliente" header="Cliente" :sortable="true">
        <template #body="{ data }">
          <span 
            class="cursor-pointer hover:text-primary"
            @click="goToClientDetail(data)"
          >
            {{ data.ragione_sociale ? data.ragione_sociale : `${data.nome} ${data.cognome}` }}
          </span>
        </template>
      </Column>
      <Column field="nome_agente" header="Nome Agente" :sortable="true">
        <template #body="{ data }">
          <span 
            class="cursor-pointer hover:text-primary"
            @click="goToAgentDetail(data)"
          >
            {{ data.nome_agente }}
          </span>
        </template>
      </Column>
      <Column field="cmor_finale" header="CMOR" :sortable="true"></Column>
      <Column field="data_emissione" header="Data Emissione" :sortable="true">
        <template #body="{ data }">
          {{ formatDate(data.data_emissione) }}
        </template>
      </Column>
      <Column field="data_uscita_cliente" header="Data Uscita Cliente" :sortable="true">
        <template #body="{ data }">
          {{ data.data_uscita_cliente ? formatDate(data.data_uscita_cliente) : '-' }}
        </template>
      </Column>
      <Column field="tipo" header="Tipo" :sortable="true">
      </Column>

      <Column field="data_richiesta_sospensione" header="Data Richiesta Sospensione" :sortable="true">
        <template #body="{ data }">
          {{ data.data_richiesta_sospensione ? formatDate(data.data_richiesta_sospensione) : '-' }}
        </template>
      </Column>
      <Column field="lavorata" header="Lavorata" :sortable="true">
        <template #body="{ data }">
          <span :class="{ 'text-success': data.lavorata === 1, 'text-danger': data.lavorata !== 1 }">
            {{ data.lavorata === 1 ? 'SI' : 'NO' }}
          </span>
        </template>
      </Column>
    </DataTable>

    <Dialog
      v-model:visible="showDialog"
      :modal="true"
      header="Dettaglio Lavorazione"
      :style="{ width: '50vw' }"
      :closable="true"
    >
      <div class="p-fluid">
        <div v-if="selectedItem">
          <p>Stato: {{ selectedItem.stato }}</p>
          <div v-if="selectedItem.stato === 'KO'">
            <p>Motivazioni:</p>
            <ul>
              <li
                v-for="(motivazione, index) in selectedItem.motivazioni"
                :key="index"
              >
                {{ motivazione }}
              </li>
            </ul>
          </div>
          <div v-else>
            <p>File disponibile per il download</p>
          </div>
        </div>
      </div>
    </Dialog>

    <!-- Dialog Storico File -->
    <Dialog
      v-model:visible="showFileHistoryDialog"
      :modal="true"
      header="Storico File"
      :style="{ width: '70vw' }"
      :closable="true"
    >
      <div class="p-fluid">
        <div v-if="loadingFiles" class="text-center">
          <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
          <p>Caricamento file in corso...</p>
        </div>
        <div v-else-if="Object.keys(fileHistory).length === 0" class="text-center">
          <p>Nessun file trovato</p>
        </div>
        <div v-else>
          <Accordion>
            <AccordionTab v-for="(files, day) in fileHistory" :key="day" :header="formatDate(day)">
              <div class="grid">
                <div v-for="file in files" :key="file.path" class="col-12 md:col-6 lg:col-4">
                  <p 
                    class="mb-2 cursor-pointer hover:text-primary"
                    @click="downloadFile(file.path)"
                  >
                    {{ file.name }}
                  </p>
                </div>
              </div>
            </AccordionTab>
          </Accordion>
        </div>
      </div>
    </Dialog>

    <!-- Dialog di conferma -->
    <Dialog
      v-model:visible="showConfirmSendDialog"
      :modal="true"
      header="Vuoi creare il tracciato?"
      :style="{ width: '30vw' }"
    >
      <div class="p-fluid">
        <p class="text-sm">Record selezionati: {{ selectedItems.length }}</p>
      </div>
      <template #footer>
        <Button
          label="No"
          icon="pi pi-times"
          @click="showConfirmSendDialog = false"
          class="p-button-text"
        />
        <Button
          label="Crea Tracciato"
          icon="pi pi-check"
          @click="sendSelectedItems"
          class="p-button-success"
          :loading="sending"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { getCurrentInstance, ref, onMounted, computed } from "vue";
//import Service from '@/services/ask-pratiche/pratiche-service'
import ClientiInUscitaService from "@/services/ask-management/clienti-in-uscita-service";
// import UtilityService from '@/services/ask-management/utility-service'
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';

export default {
  components: {
    Accordion,
    AccordionTab
  },
  setup() {
    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    self.$http = self.appContext.config.globalProperties.$http;
    const formatDate = self.appContext.config.globalProperties.$formatDate;

    // const service = new Service(self.$http, self.$eventBus)
    const clientiInUscitaService = new ClientiInUscitaService(
      self.$http,
      self.$eventBus
    );

    const items = ref([]);
    const loading = ref(false);
    const showDialog = ref(false);
    const selectedItem = ref(null);
    const selectedYear = ref(null);
    const selectedMonths = ref([]);
    const opzioniLavorata = ref([
      { label: "SI", value: 1 },
      { label: "NO", value: 0 },
    ]);

    const currentYear = new Date().getFullYear();
    const years = ref(
      Array.from({ length: 5 }, (_, i) => ({
        label: (currentYear - i).toString(),
        value: currentYear - i,
      }))
    );

    const months = ref([
      { label: "Gennaio", value: 1 },
      { label: "Febbraio", value: 2 },
      { label: "Marzo", value: 3 },
      { label: "Aprile", value: 4 },
      { label: "Maggio", value: 5 },
      { label: "Giugno", value: 6 },
      { label: "Luglio", value: 7 },
      { label: "Agosto", value: 8 },
      { label: "Settembre", value: 9 },
      { label: "Ottobre", value: 10 },
      { label: "Novembre", value: 11 },
      { label: "Dicembre", value: 12 },
    ]);

    const isSearchEnabled = computed(() => {
      return selectedYear.value && selectedMonths.value.length > 0;
    });

    const searchQuery = ref("");
    const lavorataFilter = ref("TUTTI");
    const tipoFilter = ref("TUTTI");

    const lavorataFilterOptions = ref([
      { label: "Tutti", value: "TUTTI" },
      { label: "SI", value: "SI" },
      { label: "NO", value: "NO" }
    ]);

    const tipoFilterOptions = ref([
      { label: "Tutti", value: "TUTTI" },
      { label: "VT4", value: "A" },
      { label: "Sospensione", value: "S" }
    ]);

    const filteredItems = computed(() => {
      let filtered = items.value;

      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(item => {
          return (
            (item.code && item.code.toLowerCase().includes(query)) ||
            (item.ragione_sociale && item.ragione_sociale.toLowerCase().includes(query)) ||
            (item.nome && item.nome.toLowerCase().includes(query)) ||
            (item.cognome && item.cognome.toLowerCase().includes(query)) ||
            (item.nome_agente && item.nome_agente.toLowerCase().includes(query)) ||
            (item.cmor_finale && item.cmor_finale.toString().includes(query)) ||
            (item.data_emissione && item.data_emissione.toLowerCase().includes(query)) ||
            (item.data_uscita_cliente && item.data_uscita_cliente.toLowerCase().includes(query)) ||
            (item.tipo && item.tipo.toLowerCase().includes(query)) ||
            (item.data_richiesta_sospensione && item.data_richiesta_sospensione.toLowerCase().includes(query))
          );
        });
      }

      if (lavorataFilter.value !== "TUTTI") {
        filtered = filtered.filter(item => {
          const itemLavorata = item.lavorata === 1 ? "SI" : "NO";
          return itemLavorata === lavorataFilter.value;
        });
      }

      if (tipoFilter.value !== "TUTTI") {
        filtered = filtered.filter(item => item.tipo === tipoFilter.value);
      }

      return filtered;
    });

    const attivaModifica = (data) => {
      data.isEditing = true;
    };

    const salvaStatoLavorata = async (data) => {
      try {
        data.isEditing = false;

        if (data.lavorata === 1) {
          data.lavorata = 1;
          const idx = selectedItems.value.findIndex(item => item.id === data.id);
          if (idx !== -1) {
            selectedItems.value.splice(idx, 1);
          }
          selectedItem.value = {
            ...data,
            stato: "OK",
            motivazioni: [],
          };
          showDialog.value = true;
        }
      } catch (error) {
        console.error("Errore nel salvataggio dello stato:", error);
        self.$eventBus.emit("show-error", "Errore nel salvataggio dello stato");
      }
    };

    const loadData = async () => {
      if (!isSearchEnabled.value) {
        self.$eventBus.emit('show-error', 'Seleziona anno e almeno un mese');
        return;
      }

      loading.value = true;
      try {
        const response = await clientiInUscitaService.clientiInUscita(
          selectedYear.value,
          selectedMonths.value
        );
        
        const allItems = [
          ...(response.data.vt4 || []),
          ...(response.data.sospensioni || [])
        ];

        items.value = allItems.map((item, index) => ({
          ...item,
          id: `${item.cod_pod}_${item.cod_cliente}_${item.tipo}_${index}`,
          lavorata: item.lavorata || 0,
          isEditing: false
        }));

        selectedItems.value = [];

        console.log('Contatori:', {
          vt4: response.count.vt4,
          sospensioni: response.count.sospensioni,
          totale: items.value.length
        });
      } catch (error) {
        console.error('Errore nel caricamento dei dati:', error);
        items.value = []; 
      } finally {
        loading.value = false;
      }
    };

    const selectedItems = ref([]);
    const showConfirmSendDialog = ref(false);
    const sending = ref(false);
    const showFileHistoryDialog = ref(false);
    const fileHistory = ref({});
    const loadingFiles = ref(false);

    const loadFileHistory = async () => {
      showFileHistoryDialog.value = true;
      loadingFiles.value = true;
      try {
        fileHistory.value = await clientiInUscitaService.getFileHistory();
      } catch (error) {
        console.error('Errore nel recupero dello storico file:', error);
        self.$eventBus.emit('show-error', 'Errore nel recupero dello storico file');
      } finally {
        loadingFiles.value = false;
      }
    };

    const downloadFile = async (path) => {
      try {
        const blob = await clientiInUscitaService.downloadFile(path);
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = path.split('/').pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        console.error('Errore nel download del file:', error);
        self.$eventBus.emit('show-error', 'Errore nel download del file');
      }
    };

    const goToDetail = (data) => {
      const baseUrl = window.location.origin;
      const type = data.tipo === 'A' ? 'pods' : 'pdrs';
      const id = data.tipo === 'A' ? data.cod_pod : data.cod_pdr;
      const url = `${baseUrl}/ask/${type}/${id}/dettagli`;
      window.open(url, '_blank');
    };

    const goToClientDetail = (data) => {
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/ask/clienti/${data.cod_cliente}/dettagli`;
      window.open(url, '_blank');
    };

    const goToAgentDetail = (data) => {
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/ask/agenti/${data.cod_agente}/dettagli`;
      window.open(url, '_blank');
    };

    const showConfirmDialog = () => {
      if (selectedItems.value.length > 0) {
        showConfirmSendDialog.value = true;
      }
    };

    const sendSelectedItems = async () => {
      sending.value = true;
      try {
        const itemsToSend = selectedItems.value.map(item => ({
          cod_pod: item.cod_pod,
          cod_pdr: item.cod_pdr || 0,
          cod_cliente: item.cod_cliente,
          cod_agente: item.cod_agente,
          cmor: item.cmor_finale,
          numerofattura: item.numerofattura,
          data_emissione: item.data_emissione,
          data_uscita_cliente: item.data_uscita_cliente,
          data_richiesta_sospensione: item.data_richiesta_sospensione,
          tipologia: item.tipo,
          lavorata: 1
        }));

        console.log('Record da inviare:', itemsToSend);
        await clientiInUscitaService.sendItems(itemsToSend);
        self.$eventBus.emit('show-success', 'Record inviati con successo');
        showConfirmSendDialog.value = false;
        selectedItems.value = [];
        await loadData();
      } catch (error) {
        console.error('Errore nell\'invio dei record:', error);
        self.$eventBus.emit('show-error', 'Errore nell\'invio dei record');
      } finally {
        sending.value = false;
      }
    };

    const toggleSelection = (data) => {
      const index = selectedItems.value.findIndex(item => item.id === data.id);
      if (index === -1) {
        selectedItems.value.push(data);
      } else {
        selectedItems.value.splice(index, 1);
      }
    };

    const toggleSelectAll = () => {
      const selectable = filteredItems.value.filter(item => item.lavorata !== 1);
      if (selectable.length === selectedItems.value.length) {
        selectedItems.value = [];
      } else {
        selectedItems.value = [...selectable];
      }
    };

    const allSelected = computed(() => {
      const selectable = filteredItems.value.filter(item => item.lavorata !== 1);
      return selectable.length > 0 && selectable.length === selectedItems.value.length;
    });

    onMounted(() => {
      loadData();
    });

    return {
      items,
      loading,
      formatDate,
      opzioniLavorata,
      attivaModifica,
      salvaStatoLavorata,
      showDialog,
      selectedItem,
      selectedYear,
      selectedMonths,
      years,
      months,
      isSearchEnabled,
      loadData,
      searchQuery,
      lavorataFilter,
      lavorataFilterOptions,
      tipoFilter,
      tipoFilterOptions,
      filteredItems,
      selectedItems,
      showConfirmSendDialog,
      sending,
      showConfirmDialog,
      sendSelectedItems,
      toggleSelection,
      toggleSelectAll,
      allSelected,
      showFileHistoryDialog,
      fileHistory,
      loadingFiles,
      downloadFile,
      loadFileHistory,
      goToDetail,
      goToClientDetail,
      goToAgentDetail
    };
  },
};
</script>

<style scoped>
.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.cursor-pointer {
  cursor: pointer;
}

.p-fluid {
  padding: 1rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.gap-3 {
  gap: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

:deep(.p-inputtext) {
  width: 100%;
}

.flex-grow-1 {
  flex-grow: 1;
}

.text-sm {
  font-size: 0.875rem;
  color: #666;
}

.text-success {
  color: #2196F3;
  font-weight: bold;
}
.text-danger {
  color: #888;
}

.border-1 {
  border-width: 1px;
}

.surface-border {
  border-color: var(--surface-border);
}

.border-round {
  border-radius: 6px;
}

.hover\:text-primary:hover {
  color: var(--primary-color);
}

:deep(.p-accordion-content) {
  padding: 1rem;
}

:deep(.p-accordion-header-link) {
  font-weight: 600;
}
</style>

